import { StandardEvents } from '@shopify/events';

/**
 * <amaya-quick-add>
 * Lives inside an Amaya product card. Colour chips (optional) + size pills.
 * Tapping a size sets the hidden variant id and submits Horizon's product-form-component,
 * so the bag drawer opens and refreshes exactly like on the product page.
 */
class AmayaQuickAdd extends HTMLElement {
  connectedCallback() {
    // The element can upgrade before its children are parsed; wait for them.
    if (this.querySelector('[data-amaya-variants]')) this.init();
    else if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    else requestAnimationFrame(() => this.init());
  }

  init() {
    if (this.ready) return;
    this.ready = true;
    const data = this.querySelector('[data-amaya-variants]');
    try { this.variants = JSON.parse(data?.textContent || '[]'); } catch (e) { this.variants = []; }
    const imgs = this.querySelector('[data-amaya-images]');
    try { this.images = JSON.parse(imgs?.textContent || '[]'); } catch (e) { this.images = []; }
    this.colourIndex = parseInt(this.dataset.colourIndex ?? '-1', 10);
    this.sizeIndex = parseInt(this.dataset.sizeIndex ?? '-1', 10);
    this.input = this.querySelector('input[name="id"]');
    this.form = this.querySelector('form');
    this.card = this.closest('.amaya-card');
    this.cardImgs = ['.amaya-card__img--1', '.amaya-card__img--2'].map((sel) => this.card?.querySelector(sel)).filter(Boolean);
    this.cardImgs.forEach((img) => {
      if (img.dataset.amayaSrc === undefined) {
        img.dataset.amayaSrc = img.getAttribute('src') || '';
        img.dataset.amayaSrcset = img.getAttribute('srcset') || '';
        img.dataset.amayaSizes = img.getAttribute('sizes') || '';
      }
    });
    this.hint = this.querySelector('[data-amaya-hint]');
    this.hintDefault = this.hint?.textContent || '';
    this.colourBtns = Array.from(this.querySelectorAll('[data-amaya-colour]'));
    this.sizeBtns = Array.from(this.querySelectorAll('[data-amaya-size]'));

    const active = this.colourBtns.find((b) => b.classList.contains('is-active')) || this.colourBtns[0];
    if (active) {
      this.colour = active.dataset.amayaColour;
      active.classList.add('is-active');
    } else if (this.colourIndex >= 0 && this.variants[0]) {
      // Colour option with a single value: no chips rendered, take it from the first variant.
      this.colour = this.variants[0].options[this.colourIndex];
    } else {
      this.colour = null;
    }

    this.colourBtns.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.setColour(b.dataset.amayaColour); }));
    this.sizeBtns.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.add(b); }));

    const toggle = this.card?.querySelector('[data-amaya-quick-toggle]');
    toggle?.addEventListener('click', (e) => {
      e.preventDefault();
      this.card.classList.toggle('is-quick-open');
    });
    this.onDocClick = (e) => {
      if (this.card && !this.card.contains(e.target)) this.card.classList.remove('is-quick-open');
    };
    document.addEventListener('click', this.onDocClick);

    this.onCart = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      const btn = this.pending;
      event.promise
        .then(() => {
          if (btn) { btn.classList.remove('is-adding'); btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 1600); }
          if (this.hint) this.hint.textContent = 'Added to your bag';
          setTimeout(() => { if (this.hint) this.hint.textContent = this.hintDefault; this.card?.classList.remove('is-quick-open'); }, 1600);
        })
        .catch(() => {
          if (btn) btn.classList.remove('is-adding');
          if (this.hint) this.hint.textContent = 'Something went wrong, try again';
        });
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCart);

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCart);
  }

  match(colour, size) {
    return this.variants.find((v) =>
      (this.colourIndex < 0 || v.options[this.colourIndex] === colour) &&
      (this.sizeIndex < 0 || size === '' || v.options[this.sizeIndex] === size)
    );
  }

  setColour(colour) {
    this.colour = colour;
    this.colourBtns.forEach((b) => {
      const on = b.dataset.amayaColour === colour;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // Photo for this colour: the variant's own image, else the first product image whose alt text names the colour.
    const withImage = this.variants.find((v) => v.options[this.colourIndex] === colour && v.image);
    let src = withImage ? withImage.image : null;
    if (!src && this.images.length) {
      const key = String(colour).toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp('\\b' + key + '\\b');
      const hit = this.images.find((im) => im.alt && re.test(im.alt.toLowerCase()));
      if (hit) src = hit.src;
    }
    (this.cardImgs || []).forEach((img) => {
      if (src) { img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = src; return; }
      if (img.dataset.amayaSrcset) img.setAttribute('srcset', img.dataset.amayaSrcset);
      if (img.dataset.amayaSizes) img.setAttribute('sizes', img.dataset.amayaSizes);
      if (img.dataset.amayaSrc) img.src = img.dataset.amayaSrc;
    });
    this.refresh();
  }

  refresh() {
    this.sizeBtns.forEach((b) => {
      const v = this.match(this.colour, b.dataset.amayaSize);
      const ok = !!v && v.available;
      b.disabled = !ok;
      b.classList.toggle('is-soldout', !ok);
    });
  }

  add(btn) {
    const v = this.match(this.colour, btn.dataset.amayaSize);
    if (!v || !v.available || !this.input || !this.form) return;
    this.input.value = String(v.id);
    this.pending = btn;
    btn.classList.add('is-adding');
    if (this.hint) this.hint.textContent = 'Adding…';
    if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
    else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }
}

if (!customElements.get('amaya-quick-add')) {
  customElements.define('amaya-quick-add', AmayaQuickAdd);
}
